import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_GUIDE_MIGRATIONS } from './guideArchive.graphql';
import { GuideGroup, Migration, StatusFilter, SortColumn, SortDirection } from './types';

export const useGuideMigrations = (statusFilter: StatusFilter, searchQuery: string, sortColumn: SortColumn, sortDirection: SortDirection, ownerFilter: string) => {
  const { data, loading } = useQuery(GET_GUIDE_MIGRATIONS);
  const migrations = data?.guideMigrations ?? [];
  const guides = data?.guides ?? [];

  const { filteredGroups, ownerNames, stats } = useMemo(() => {
    const ownerByGuideId = new Map(guides.map((g) => [g?.id, g?.owner?.name]));
    const categoryByGuideId = new Map(guides.map((g) => [g?.id, g?.mainCategory?.title]));

    const grouped = migrations.reduce(
      (acc, migration) => {
        const key = migration?.guideId;
        (acc[key] ??= []).push(migration);
        return acc;
      },
      {} as Record<string, Migration[]>,
    );

    const groups: GuideGroup[] = Object.entries(grouped).map(([guideId, migrations]) => {
      const publishedCount = migrations.filter((m) => m?.publishedAt).length;
      return {
        guideId,
        migrations,
        publishedCount,
        allPublished: publishedCount === migrations.length,
        ownerName: ownerByGuideId.get(guideId),
        mainCategory: categoryByGuideId.get(guideId),
      };
    });

    // Sort by selected column
    groups.sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'docName') {
        comparison = (a.migrations[0]?.docName ?? '').localeCompare(b.migrations[0]?.docName ?? '');
      } else if (sortColumn === 'title') {
        comparison = (a.migrations[0]?.title ?? '').localeCompare(b.migrations[0]?.title ?? '');
      } else if (sortColumn === 'status') {
        const aProgress = a.publishedCount / a.migrations.length;
        const bProgress = b.publishedCount / b.migrations.length;
        comparison = aProgress - bProgress;
      } else if (sortColumn === 'ownerName') {
        comparison = (a.ownerName ?? '').localeCompare(b.ownerName ?? '');
      }
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    // Collect unique owner names (before filtering)
    const ownerNames = [...new Set(groups.map((g) => g.ownerName).filter((name): name is string => !!name))].sort((a, b) =>
      a.localeCompare(b),
    );

    // Apply status filter
    let filtered = groups.filter((group) => {
      if (statusFilter === 'unpublished') return !group.allPublished;
      if (statusFilter === 'published') return group.allPublished;
      return true;
    });

    // Apply owner filter
    if (ownerFilter) {
      filtered = filtered.filter((group) => group.ownerName === ownerFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((group) => {
        const docName = group.migrations[0]?.docName?.toLowerCase() ?? '';
        const title = group.migrations[0]?.title?.toLowerCase() ?? '';
        return docName.includes(query) || title.includes(query);
      });
    }

    const totalPublished = migrations.filter((m) => m?.publishedAt).length;
    const completedGuides = groups.filter((g) => g.allPublished).length;

    return {
      filteredGroups: filtered,
      ownerNames,
      stats: {
        totalMigrations: migrations.length,
        totalPublished,
        totalGuides: groups.length,
        completedGuides,
        incompleteGuides: groups.length - completedGuides,
        progressPercent: migrations.length > 0 ? Math.round((totalPublished / migrations.length) * 100) : 0,
      },
    };
  }, [migrations, guides, statusFilter, searchQuery, sortColumn, sortDirection, ownerFilter]);

  return { filteredGroups, ownerNames, stats, loading: loading && migrations.length === 0 };
};
